class VariantSelects extends HTMLElement {
  constructor() {
    super();
    this.addEventListener('change', this.onVariantChange);
  }

  connectedCallback() {
    // Retain the authenticated draft route for the six October YOKKAO drafts.
    // Public product URLs and every other product keep their existing behavior.
    const draftProductIds = ["8931824631945", "8931825877129", "8931826270345", "8931826729097", "8931826892937", "8931827187849"];
    const previewUrl = new URL(window.location.href);
    if (draftProductIds.includes(this.dataset.productId) &&
        previewUrl.hostname.endsWith('.shopifypreview.com') &&
        previewUrl.pathname === '/products_preview' &&
        previewUrl.searchParams.has('preview_key')) {
      this.mmaDraftPreviewUrl = previewUrl.href;
    }
    this.updateDiscountedOptionStates();
  }

  variantRequestUrl(sectionId) {
    if (!this.mmaDraftPreviewUrl) {
      return `${this.dataset.url}?variant=${this.currentVariant.id}${sectionId ? `&section_id=${sectionId}` : ''}`;
    }
    const url = new URL(this.mmaDraftPreviewUrl);
    url.searchParams.set('variant', this.currentVariant.id);
    url.searchParams.delete('section_id');
    if (sectionId) url.searchParams.set('section_id', sectionId);
    return url.pathname + url.search;
  }

  onVariantChange(e) {
    const { isCombinedProduct, targetUrl } = this.combinedListingCheck(e);

    this.toggleAddButton(true, '', false);

    if (isCombinedProduct) {
      this.fetchProductSection(targetUrl);
      return;  // Stop execution if false
    }

    this.updateOptions();
    this.updateMasterId();
    this.updatePickupAvailability();
    this.updateInventoryStatus();
    this.removeErrorMessage();
    this.updateVariantStatuses();
    this.updateDiscountedOptionStates();

    if (!this.currentVariant) {
      this.toggleAddButton(true, '', true);
      this.setUnavailable();
    } else {
      this.updateMedia();
      this.updateURL();
      this.updateVariantInput();
      this.updateInstallmentTeasers();
      this.renderProductInfo();
      this.updateShareUrl();
      this.updateSaleBadge();
    }
  }

  combinedListingCheck(e) {
    const currentUrl = window.location.pathname;
    const targetUrl = e.target.dataset.productUrl;
    let isCombinedProduct = false;

    if (targetUrl != undefined && currentUrl != targetUrl) {
      isCombinedProduct = true;
    }

    return { isCombinedProduct, targetUrl };
  }

  async fetchProductSection(newProductUrl) {
    const productElement = this.closest('.main-product');
    const productSectionId = this.dataset.section;
    let params = `section_id=${productSectionId}&`;

    const selectedOptionValues = Array.from(this.querySelectorAll('fieldset input:checked')).map((element) => element.dataset.optionValueId).join(',');
      params += `option_values=${selectedOptionValues}`;

    fetch(`${newProductUrl}?${params}`)
      .then((response) => response.text())
      .then((responseText) => {
        const html = new DOMParser().parseFromString(responseText, 'text/html');
        productElement.innerHTML = html.querySelector('.main-product').innerHTML;
        initPdpDeliveryEstimator();
        window.history.replaceState({ }, '', `${newProductUrl}`);
      });
  }

  updateOptions() {
    this.options = Array.from(this.querySelectorAll('select'), (select) => select.value);
  }

  updateMasterId() {
    this.currentVariant = this.getVariantData().find((variant) => {
      return !variant.options.map((option, index) => {
        return this.options[index] === option;
      }).includes(false);
    });
  }

  updateMedia() {
    if (!this.currentVariant) return;
    if (!this.currentVariant.featured_media) return;

    const mediaGridySlider = document.getElementById(`slider-${this.dataset.section}`);
    const thumbnailsGridySlider = document.getElementById(`thumbnail-slider-${this.dataset.section}`);

    if (mediaGridySlider) {
      mediaGridySlider.scrollToSlideByID(
        `item-${this.currentVariant.featured_media.id}`
      );
    }

    if (thumbnailsGridySlider) {
      thumbnailsGridySlider.scrollToSlideByID(`thumbnail-item-${this.currentVariant.featured_media.id}`)
    }

    const isDesktop = window.matchMedia('(min-width: 1024px)').matches;
    const isGrid = mediaGridySlider.dataset.desktopEnabled === 'false' ? true : false;
    if (isDesktop && isGrid) {
      document.getElementById(`item-${this.currentVariant.featured_media.id}`)?.scrollIntoView();
    }
  }

  updateURL() {
    if (!this.currentVariant || this.dataset.updateUrl === 'false') return;
    window.history.replaceState({ }, '', this.variantRequestUrl());
  }

  updateShareUrl() {
    const shareButton = document.getElementById(`Share-${this.dataset.section}`);
    if (!shareButton || !shareButton.updateUrl) return;
    shareButton.updateUrl(`${window.shopUrl}${this.dataset.url}?variant=${this.currentVariant.id}`);
  }

  updateSaleBadge() {
    if (!this.currentVariant) return;

    const saleBadge = document.getElementById('saleBadge');
    if (!saleBadge) return;

    const comparePrice = this.currentVariant.compare_at_price;
    const salePrice = this.currentVariant.price;

    if (comparePrice && comparePrice > salePrice) {
      const badgeType = saleBadge.dataset.saleBadgeType || 'default';
      let badgeText = '';

      if (badgeType === 'savings') {
        // Calculate savings amount
        const savingsAmount = comparePrice - salePrice;
        const formattedSavings = Woolman.Utils.formatMoney(savingsAmount);
        badgeText = window.saleBadgeStrings.savings.replace('[amount]', formattedSavings);
      } else if (badgeType === 'percentage') {
        // Calculate discount percentage
        const discountPercentage = Math.floor(((comparePrice - salePrice) / comparePrice) * 100);
        badgeText = window.saleBadgeStrings.percentage.replace('[percentage]', discountPercentage);
      } else {
        // Default badge type
        badgeText = window.saleBadgeStrings.default;
      }

      saleBadge.textContent = badgeText;
      saleBadge.classList.remove('hide');
    } else {
      // Hide badge if no discount
      saleBadge.classList.add('hide');
    }
  }

  updateDiscountedOptionStates() {
    const optionGroups = Array.from(
      this.querySelectorAll('fieldset.product-form__input')
    );

    if (!optionGroups.length) return;

    const selectedOptions = optionGroups.map((group) => {
      return group.querySelector('input[type="radio"]:checked')?.value;
    });
    const variants = this.getVariantData();
    const states = optionGroups.map((group, optionIndex) => {
      const inputs = Array.from(
        group.querySelectorAll('input[type="radio"]')
      );
      const options = inputs.map((input) => {
        const candidateOptions = [...selectedOptions];
        candidateOptions[optionIndex] = input.value;
        const variant = variants.find((item) => {
          return item.options.every(
            (value, index) => value === candidateOptions[index]
          );
        });
        const comparePrice = Number(variant?.compare_at_price || 0);
        const salePrice = Number(variant?.price || 0);

        return {
          input,
          discounted: Boolean(
            variant?.available && comparePrice > salePrice
          )
        };
      });

      return {
        group,
        role: group.dataset.optionRole || 'default',
        options,
        hasDiscount: options.some((option) => option.discounted)
      };
    });
    const primaryState =
      states.find((state) => state.role === 'size' && state.hasDiscount)
      || states.find((state) => state.role === 'color' && state.hasDiscount)
      || states.find((state) => state.hasDiscount);

    states.forEach((state) => {
      state.options.forEach(({ input, discounted }) => {
        const label = input.nextElementSibling?.matches('label')
          ? input.nextElementSibling
          : null;
        const cue = label?.querySelector('[data-variant-discount-cue]');
        const shouldShow = state === primaryState && discounted;

        if (!label || !cue) return;

        label.classList.toggle('is-discounted-variant-option', shouldShow);
        input.dataset.discountedVariant = shouldShow ? 'true' : 'false';
        cue.hidden = !shouldShow;
      });
    });
  }

  updateVariantInput() {
    const productForms = document.querySelectorAll(`#product-form-${this.dataset.section}, #product-form-installment-${this.dataset.section}, #product-form-installment-mobile-${this.dataset.section}, #product-form-${this.dataset.section}-product-buy-bar`);
    productForms.forEach((productForm) => {
      const input = productForm.querySelector('input[name="id"]');
      input.value = this.currentVariant.id;
      input.dispatchEvent(new Event('change', { bubbles: true }));
    });
  }

  updateInstallmentTeasers() {
    if (!this.currentVariant) return;

    const price = Number(this.currentVariant.price || 0);
    let terms = 0;

    if (price >= 3500 && price < 5000) {
      terms = 2;
    } else if (price >= 5000 && price < 100000) {
      terms = 4;
    }

    document.querySelectorAll('[data-pdp-installment-teaser]').forEach((teaser) => {
      if (teaser.dataset.pdpInstallmentSuppressed === 'gift-card') {
        teaser.classList.add('is-hidden', 'is-suppressed');
        return;
      }

      const amount = teaser.querySelector('[data-pdp-installment-amount]');
      teaser.classList.toggle('is-hidden', terms === 0);
      teaser.classList.toggle('is-suppressed', price < 3500);

      if (terms === 0) return;

      const installmentAmount = Math.round(price / terms);
      const dollars = Math.floor(installmentAmount / 100);
      const cents = String(installmentAmount % 100).padStart(2, '0');
      const priceElement = teaser.querySelector('[data-pdp-installment-price]');
      const dollarsElement = teaser.querySelector('[data-pdp-installment-dollars]');
      const centsElement = teaser.querySelector('[data-pdp-installment-cents]');

      if (amount) {
        amount.textContent = new Intl.NumberFormat('en-US', {
          style: 'currency',
          currency: 'USD'
        }).format(installmentAmount / 100);
      }

      if (priceElement) priceElement.setAttribute('aria-label', `$${dollars}.${cents}`);
      if (dollarsElement) dollarsElement.textContent = dollars;
      if (centsElement) centsElement.textContent = cents;
    });
  }

  updateVariantStatuses() {
    const selectedOptionOneVariants = this.variantData.filter(variant => this.querySelector(':checked').value === variant.option1);
    const inputWrappers = [...this.querySelectorAll('.product-form__input')];
    inputWrappers.forEach((option, index) => {
      if (index === 0) return;
      const optionInputs = [...option.querySelectorAll('input[type="radio"], option')]
      const previousOptionSelected = inputWrappers[index - 1].querySelector(':checked').value;
      const availableOptionInputsValue = selectedOptionOneVariants.filter(variant => variant.available && variant[`option${ index }`] === previousOptionSelected).map(variantOption => variantOption[`option${ index + 1 }`]);
      this.setInputAvailability(optionInputs, availableOptionInputsValue)
    });
  }

  setInputAvailability(listOfOptions, listOfAvailableOptions) {
    listOfOptions.forEach(input => {
      if (listOfAvailableOptions.includes(input.getAttribute('value'))) {
        input.innerText = input.getAttribute('value');
      } else {
        input.innerText = window.variantStrings.unavailable_with_option.replace('[value]', input.getAttribute('value'));
      }
    });
  }

  updatePickupAvailability() {
    const pickUpAvailability = document.querySelector('pickup-availability');
    if (!pickUpAvailability) return;

    if (this.currentVariant && this.currentVariant.available) {
      pickUpAvailability.fetchAvailability(this.currentVariant.id);
    } else {
      pickUpAvailability.removeAttribute('available');
      pickUpAvailability.innerHTML = '';
    }
  }

  updateInventoryStatus() {
    this.productInventoryNode = document.querySelector('[data-product-inventory]');

    if (!this.productInventoryNode || !window.inventories || !window.inventories[this.dataset.productId]) return;

    this.inventoryThreshold = parseInt(this.productInventoryNode.dataset.threshold);
    this.showQuantityInStock = this.productInventoryNode.dataset.showQuantityInStock;

    const variantInventoryObject = window.inventories[this.dataset.productId][this.currentVariant?.id];

    if (!this.currentVariant || !this.currentVariant.inventory_management) {
      this.toggleInventoryQuantity(false);
      this.toggleIncomingInventory(false);

      return;
    }

    if (this.currentVariant.inventory_management === 'shopify') {
      if (variantInventoryObject.quantity <= 0 && variantInventoryObject.incoming === 'true' && variantInventoryObject.quantity < this.inventoryThreshold) {
        this.toggleInventoryQuantity(false);
        this.toggleIncomingInventory(true, variantInventoryObject.next_incoming_date);
      } else {
        this.toggleInventoryQuantity(true, variantInventoryObject.quantity);
        this.toggleIncomingInventory(false);
      }
    }
  }

  toggleInventoryQuantity(show, quantity) {
    if (show === false) {
      this.productInventoryNode.classList.add('hide');
      return;
    }

    this.productInventoryNode.classList.remove('inventory-status--in-stock', 'inventory-status--low', 'inventory-status--sold-out', 'inventory-status--sold-out-continue-selling');

    if (this.currentVariant && this.currentVariant.available && parseInt(quantity) <= parseInt(this.inventoryThreshold) && parseInt(quantity) > 0) {
      this.productInventoryNode.classList.add('inventory-status--low');
      if (this.showQuantityInStock === 'true') {
        this.productInventoryNode.firstElementChild.textContent = window.variantStrings.inventory.few_left_with_quantity.replace('[quantity]', quantity);
      } else {
        this.productInventoryNode.firstElementChild.textContent = window.variantStrings.inventory.few_left;
      }
    } else if (this.currentVariant && this.currentVariant.available && parseInt(quantity) > parseInt(this.inventoryThreshold)) {
      this.productInventoryNode.classList.add('inventory-status--in-stock');
      if (this.showQuantityInStock === 'true') {
        this.productInventoryNode.firstElementChild.textContent = window.variantStrings.inventory.in_stock_with_quantity.replace('[quantity]', quantity);
      } else {
        this.productInventoryNode.firstElementChild.textContent = window.variantStrings.inventory.in_stock;
      }
    } else if (this.currentVariant && this.currentVariant.available && parseInt(quantity) < 1) {
      this.productInventoryNode.classList.add('inventory-status--sold-out-continue-selling');
      this.productInventoryNode.firstElementChild.textContent = window.variantStrings.inventory.out_of_stock_continue_selling;
    } else {
      this.productInventoryNode.classList.add('inventory-status--sold-out');
      this.productInventoryNode.firstElementChild.textContent = window.variantStrings.inventory.out_of_stock;
    }

    this.productInventoryNode.classList.remove('hide');
  }

  toggleIncomingInventory(show, date) {
    const incomingInventoryNode = document.querySelector('[data-incoming-inventory]');
    if (!incomingInventoryNode) return;

    if (show == false && incomingInventoryNode) {
      incomingInventoryNode.classList.add('hide');

      return;
    }

    let string = !date ?
      window.variantStrings.inventory.waiting_for_stock :
      window.variantStrings.inventory.will_be_in_stock_after.replace('[date]', date);

    incomingInventoryNode.classList.remove('hide');
    incomingInventoryNode.firstElementChild.textContent = string;
  }

  removeErrorMessage() {
    const section = this.closest('section');
    if (!section) return;

    const productForm = section.querySelector('product-form');
    if (productForm) productForm.handleErrorMessage();
  }

  renderProductInfo() {
    fetch(this.variantRequestUrl(this.dataset.originalSection || this.dataset.section))
      .then((response) => response.text())
      .then((responseText) => {
        const html = new DOMParser().parseFromString(responseText, 'text/html');
        const destination = document.getElementById(`price-${this.dataset.section}`);
        const mobileDestination = document.getElementById(`price-mobile-${this.dataset.section}`);
        const skuSource = html.getElementById(
          `Sku-${this.dataset.originalSection ? this.dataset.originalSection : this.dataset.section}`
        );
        const skuDestination = document.getElementById(`Sku-${this.dataset.section}`);
        const destinationBuyBarInfo = document.getElementById(
          `buy-bar-info-${this.dataset.section}`
        );
        const source = html.getElementById(`price-${this.dataset.originalSection ? this.dataset.originalSection : this.dataset.section}`);
        const mobileSource = html.getElementById(`price-mobile-${this.dataset.originalSection ? this.dataset.originalSection : this.dataset.section}`);
        const buyBarInfoSource = html.getElementById(`buy-bar-info-${this.dataset.originalSection ? this.dataset.originalSection : this.dataset.section}`);
        if (source && destination) destination.innerHTML = source.innerHTML;
        if (mobileSource && mobileDestination) mobileDestination.innerHTML = mobileSource.innerHTML;
        if (source && destinationBuyBarInfo) destinationBuyBarInfo.innerHTML = buyBarInfoSource.innerHTML;

        if (skuSource && skuDestination) {
          skuDestination.innerHTML = skuSource.innerHTML;
          skuDestination.classList.toggle('hidden', skuSource.classList.contains('hidden'));
        }

        const price = document.getElementById(`price-${this.dataset.section}`);
        const mobilePrice = document.getElementById(`price-mobile-${this.dataset.section}`);

        if (price) price.classList.remove('hidden');
        if (mobilePrice) mobilePrice.classList.remove('hidden');
        this.toggleAddButton(!this.currentVariant.available, window.variantStrings.soldOut);
      });
  }

  toggleAddButton(disable = true, text, modifyClass = true) {
    const productForm = document.getElementById(`product-form-${this.dataset.section}`);
    if (!productForm) return;
    const addButton = productForm.querySelector('[name="add"]');
    const addButtonText = productForm.querySelector('[name="add"] > span');
    const multiplyButtons = productForm.querySelectorAll('.btn--add-multiplier');
    if (!addButton) return;

    if (disable) {
      addButton.setAttribute('disabled', 'disabled');
      if (text) addButtonText.textContent = text;
      if (multiplyButtons) {
        multiplyButtons.forEach(button => {
          button.disabled = true;
        });
      }
    } else {
      addButton.removeAttribute('disabled');
      if (this.dataset.isPreorder == 'true') {
        addButtonText.textContent = window.variantStrings.preOrder;
      } else {
        addButtonText.textContent = window.variantStrings.addToCart;
      }
      if (multiplyButtons) {
        multiplyButtons.forEach(button => {
          button.removeAttribute('disabled');
        });
      }
    }

    const productForm_BuyBar = document.getElementById(
      `product-form-${this.dataset.section}-product-buy-bar`
    );
    if (productForm_BuyBar) {
      const addButton_BuyBar = productForm_BuyBar.querySelector('[name="add"]');
      const addButtonText_BuyBar = productForm_BuyBar.querySelector(
        '[name="add"] > span'
      );

      if (disable) {
        addButton_BuyBar.setAttribute("disabled", "disabled");
        if (text) addButtonText_BuyBar.textContent = text;
      } else {
        addButton_BuyBar.removeAttribute("disabled");
        if (this.dataset.isPreorder == "true") {
          addButtonText_BuyBar.textContent = window.variantStrings.preOrder;
        } else {
          addButtonText_BuyBar.textContent = window.variantStrings.addToCart;
        }
      }
    }

    if (!modifyClass) return;
  }

  setUnavailable() {
    const button = document.getElementById(`product-form-${this.dataset.section}`);
    const addButton = button.querySelector('[name="add"]');
    const addButtonText = button.querySelector('[name="add"] > span');
    const price = document.getElementById(`price-${this.dataset.section}`);
    const mobilePrice = document.getElementById(`price-mobile-${this.dataset.section}`);
    if (!addButton) return;
    addButtonText.textContent = window.variantStrings.unavailable;
    if (price) price.classList.add('hidden');
    if (mobilePrice) mobilePrice.classList.add('hidden');

    const button_BuyBar = document.getElementById(
      `product-form-${this.dataset.section}-product-buy-bar`
    );
    const addButton_BuyBar = button_BuyBar.querySelector('[name="add"]');
    const addButtonText_BuyBar = button_BuyBar.querySelector(
      '[name="add"] > span'
    );
    const price_BuyBar = document.getElementById(
      `price-${this.dataset.section}-product-buy-bar`
    );
    if (!addButton_BuyBar) return;
    addButtonText_BuyBar.textContent = window.variantStrings.unavailable;
    if (price_BuyBar) price_BuyBar.classList.add("hidden");
  }

  getVariantData() {
    this.variantData = this.variantData || JSON.parse(this.querySelector('[type="application/json"]').textContent);
    return this.variantData;
  }
}

customElements.define('variant-selects', VariantSelects);

if (!window.__mmaPdpInstallmentLearnBound) {
  window.__mmaPdpInstallmentLearnBound = true;

  document.addEventListener('click', (event) => {
    const trigger = event.target.closest('[data-pdp-installment-learn]');
    if (!trigger) return;

    event.preventDefault();

    const teaser = trigger.closest('[data-pdp-installment-teaser]');
    const paymentTerms = teaser?.querySelector('shopify-payment-terms');
    const disclosureTarget = paymentTerms?.shadowRoot?.querySelector('button, a, [role="button"]')
      || paymentTerms?.querySelector('button, a, [role="button"]');

    if (disclosureTarget) {
      disclosureTarget.click();
      return;
    }

    paymentTerms?.click();
  });
}

class VariantRadios extends VariantSelects {
  constructor() {
    super();
  }

  setInputAvailability(listOfOptions, listOfAvailableOptions) {
    listOfOptions.forEach(input => {
      const unavailableCopy = input.nextElementSibling?.querySelector(
        '[data-option-unavailable]'
      );

      if (listOfAvailableOptions.includes(input.getAttribute('value'))) {
        input.classList.remove('disabled');
        if (unavailableCopy) unavailableCopy.hidden = true;
      } else {
        input.classList.add('disabled');
        if (unavailableCopy) unavailableCopy.hidden = false;
      }
    });
  }

  updateOptions() {
    const fieldsets = Array.from(this.querySelectorAll('fieldset'));
    this.options = fieldsets.map((fieldset) => {
      return Array.from(fieldset.querySelectorAll('input')).find((radio) => radio.checked).value;
    });

    this.updateLegends();
  }

  updateLegends() {
    const fieldsets = Array.from(this.querySelectorAll('fieldset'));
    fieldsets.forEach((fieldset) => {
      const legend = fieldset.querySelector('legend [data-variant-legend]');
      const selectedOption = Array.from(fieldset.querySelectorAll('input')).find((radio) => radio.checked);
      legend.textContent = selectedOption.getAttribute('value');
    });
  }
}

customElements.define('variant-radios', VariantRadios);

class ProductBuyBar extends HTMLElement {
  constructor() {
    "use strict";
    super();

    let classes = { show: "show" };
    let mainProductForm = document.querySelector(".product-form");

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            // In view -> Hide bar
            this.classList.remove(classes.show);
          } else if (window.scrollY > 200) {
            // Out of view AND scrolled a little from top -> Show bar
            this.classList.add(classes.show);
          }
        });
      },
      { threshold: 0.25 }
    );

    observer.observe(mainProductForm);

    // This script is loaded in product-template.liquid --> Footer observer after DOM ready
    document.addEventListener("DOMContentLoaded", function () {
      let siteFooter = document.querySelector("footer.footer");
      if (!siteFooter) return;
      observer.observe(siteFooter);
    });
  }
}
customElements.define("product-buy-bar", ProductBuyBar);

document.addEventListener('click', (event) => {
  const toggle = event.target.closest('[data-mobile-price-tip-toggle]');
  const currentTip = event.target.closest('.pdp-mobile-price-tip');

  document.querySelectorAll('.pdp-mobile-price-tip.is-open').forEach((tip) => {
    if (tip !== currentTip) {
      tip.classList.remove('is-open');
      tip.querySelector('[data-mobile-price-tip-toggle]')?.setAttribute('aria-expanded', 'false');
    }
  });

  if (!toggle) return;

  event.preventDefault();
  const tip = toggle.closest('.pdp-mobile-price-tip');
  const isOpen = tip.classList.toggle('is-open');
  toggle.setAttribute('aria-expanded', isOpen ? 'true' : 'false');
});

document.addEventListener('click', (event) => {
  const ratingLink = event.target.closest('.pdp-mobile-rating__count, .pdp-desktop-rating__count');
  if (!ratingLink) return;

  const reviewTarget = document.querySelector(
    '.jdgm-review-widget, .jdgm-widget.jdgm-review-widget, .jdgm-rev-widg, #judgeme_product_reviews, [id*="judge_me_reviews_review_widget"], [id*="judgeme"]'
  );

  if (!reviewTarget) return;

  event.preventDefault();
  reviewTarget.scrollIntoView({ behavior: 'smooth', block: 'start' });
});

document.addEventListener('click', (event) => {
  const toggle = event.target.closest('[data-coach-tip-toggle]');
  const currentTip = event.target.closest('.coach-tip--mobile, .coach-tip--desktop');

  document.querySelectorAll('.coach-tip--mobile.is-open, .coach-tip--desktop.is-open').forEach((tip) => {
    if (tip !== currentTip) {
      tip.classList.remove('is-open');
      tip.querySelector('[data-coach-tip-toggle]')?.setAttribute('aria-expanded', 'false');
    }
  });

  if (!toggle) return;

  event.preventDefault();
  const tip = toggle.closest('.coach-tip--mobile, .coach-tip--desktop');
  const isOpen = tip.classList.toggle('is-open');
  toggle.setAttribute('aria-expanded', isOpen ? 'true' : 'false');

  if (isOpen) {
    const tooltip = tip.querySelector('.coach-tip__text');
    if (!tooltip) return;

    tip.style.setProperty('--coach-tip-shift-x', '0px');
    tip.style.setProperty('--coach-tip-arrow-x', '50%');

    requestAnimationFrame(() => {
      const viewportPadding = 16;
      const toggleRect = toggle.getBoundingClientRect();
      const tooltipRect = tooltip.getBoundingClientRect();
      const centeredLeft = toggleRect.left + (toggleRect.width / 2) - (tooltipRect.width / 2);
      const clampedLeft = Math.min(
        Math.max(centeredLeft, viewportPadding),
        window.innerWidth - tooltipRect.width - viewportPadding
      );
      const shiftX = clampedLeft - centeredLeft;
      const arrowX = (tooltipRect.width / 2) - shiftX;

      tip.style.setProperty('--coach-tip-shift-x', `${shiftX}px`);
      tip.style.setProperty('--coach-tip-arrow-x', `${arrowX}px`);
    });
  }
});

document.addEventListener('keydown', (event) => {
  if (event.key !== 'Escape') return;

  document.querySelectorAll('.coach-tip--mobile.is-open, .coach-tip--desktop.is-open').forEach((tip) => {
    tip.classList.remove('is-open');
    tip.querySelector('[data-coach-tip-toggle]')?.setAttribute('aria-expanded', 'false');
  });
});

function initPdpDeliveryEstimator() {
  const estimators = document.querySelectorAll('[data-pdp-delivery-estimator]');
  if (!estimators.length) return;

  const timeZone = 'America/Denver';
  const cutoffHour = 14;
  const now = new Date();
  const parts = new Intl.DateTimeFormat('en-US', {
    timeZone,
    year: 'numeric',
    month: 'numeric',
    day: 'numeric',
    hour: 'numeric',
    hour12: false,
    weekday: 'short'
  }).formatToParts(now).reduce((acc, part) => {
    if (part.type !== 'literal') acc[part.type] = part.value;
    return acc;
  }, {});

  const denverToday = new Date(Date.UTC(Number(parts.year), Number(parts.month) - 1, Number(parts.day), 12));
  const denverHour = Number(parts.hour);

  function isBusinessDay(date) {
    const day = date.getUTCDay();
    return day !== 0 && day !== 6;
  }

  function addCalendarDays(date, days) {
    const next = new Date(date);
    next.setUTCDate(next.getUTCDate() + days);
    return next;
  }

  function nextBusinessDay(date) {
    let next = addCalendarDays(date, 1);
    while (!isBusinessDay(next)) {
      next = addCalendarDays(next, 1);
    }
    return next;
  }

  function addBusinessDays(date, days) {
    let next = new Date(date);
    let added = 0;
    while (added < days) {
      next = addCalendarDays(next, 1);
      if (isBusinessDay(next)) added += 1;
    }
    return next;
  }

  const shipsToday = isBusinessDay(denverToday) && denverHour < cutoffHour;
  const processingDate = shipsToday ? denverToday : nextBusinessDay(denverToday);
  const earliest = addBusinessDays(processingDate, 1);
  const latest = addBusinessDays(processingDate, 4);
  const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  const weekdays = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
  const formatDate = (date) => `${weekdays[date.getUTCDay()]}, ${months[date.getUTCMonth()]} ${date.getUTCDate()}`;

  estimators.forEach((estimator) => {
    const start = estimator.querySelector('[data-pdp-delivery-start]');
    const end = estimator.querySelector('[data-pdp-delivery-end]');
    if (start) start.textContent = formatDate(earliest);
    if (end) end.textContent = formatDate(latest);
  });
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initPdpDeliveryEstimator);
} else {
  initPdpDeliveryEstimator();
}

function initMmaJudgeReviews() {
  const widget = document.querySelector('.jdgm-review-widget[data-product-id], #judgeme_product_reviews[data-product-id]');
  if (!widget || widget.dataset.mmaEnhanced === 'true') return;

  const productId = widget.getAttribute('data-product-id') || widget.getAttribute('data-id');
  const reviewData = window.jdgm?.data?.reviewWidget?.[productId];
  const reviewCount = Number(reviewData?.number_of_reviews) || 0;
  const hasLoadedReviews = Array.isArray(reviewData?.reviews) && reviewData.reviews.length > 0;

  const hideReviewSection = () => {
    widget.dataset.mmaEnhanced = 'true';
    widget.classList.add('mma-judge-native-hidden');
    const block = widget.closest('.shopify-block, .shopify-app-block');
    if (block) block.classList.add('mma-judge-block-hidden');
    if (productId) {
      const existing = document.querySelector('.mma-judge[data-mma-judge-product-id="' + productId + '"]');
      if (existing) existing.remove();
    }
  };

  if (!productId || !reviewData || reviewCount < 1 || !hasLoadedReviews) {
    hideReviewSection();
    return;
  }

  widget.dataset.mmaEnhanced = 'true';
  widget.classList.add('mma-judge-native-hidden');
  const nativeBlock = widget.closest('.shopify-block, .shopify-app-block');
  if (nativeBlock) nativeBlock.classList.remove('mma-judge-block-hidden');

  const existing = document.querySelector('.mma-judge[data-mma-judge-product-id="' + productId + '"]');
  if (existing) existing.remove();

  const root = document.createElement('section');
  root.className = 'mma-judge';
  root.id = 'pdp-reviews';
  root.dataset.mmaJudgeProductId = productId;
  root.setAttribute('aria-label', 'Customer reviews');

  const sortOptions = {
    latest: { label: 'Latest reviews', sortBy: 'created_at', sortDir: 'desc' },
    highest: { label: 'Highest rated', sortBy: 'rating', sortDir: 'desc' },
    lowest: { label: 'Lowest rated', sortBy: 'rating', sortDir: 'asc' },
    photos: { label: 'Photos first', sortBy: 'pictures_first', sortDir: 'desc' }
  };

  const state = {
    sort: 'latest',
    reviews: Array.isArray(reviewData.reviews) ? reviewData.reviews.slice() : [],
    page: Number(reviewData.pagination?.current_page) || 1,
    totalPages: Number(reviewData.pagination?.total_pages) || 1,
    perPage: Number(reviewData.pagination?.per_page) || 5,
    visibleCount: 3,
    isLoading: false
  };

  const stripHtml = (value) => {
    const node = document.createElement('div');
    node.innerHTML = value || '';
    return (node.textContent || '').replace(/\s+/g, ' ').trim();
  };

  const stars = (rating) => {
    const score = Math.max(0, Math.min(5, Number(rating) || 0));
    return '★★★★★'.slice(0, Math.round(score)) + '☆☆☆☆☆'.slice(0, 5 - Math.round(score));
  };

  const formatDate = (value) => {
    if (!value) return '';
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return '';
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  };

  const appendText = (parent, tag, className, text) => {
    const node = document.createElement(tag);
    node.className = className;
    node.textContent = text || '';
    parent.appendChild(node);
    return node;
  };

  const dedupeReviews = (reviews) => {
    const seen = new Set();
    return reviews.filter((review) => {
      const key = review.uuid || [review.created_at, review.reviewer_name, review.title, review.body].join('|');
      if (seen.has(key)) return false;
      seen.add(key);
      return true;
    });
  };

  const getShopDomain = () => {
    return window.jdgm?.SHOP_DOMAIN || window.Shopify?.shop || document.domain;
  };

  const getApiBase = () => {
    const host = window.jdgm?.CDN_API_HOST || 'https://cdn.judge.me/';
    return host.endsWith('/') ? host : host + '/';
  };

  const buildReviewUrl = (page, sortKey = state.sort) => {
    const sort = sortOptions[sortKey] || sortOptions.latest;
    const params = new URLSearchParams({
      product_id: productId,
      page: String(page),
      sort_by: sort.sortBy,
      sort_dir: sort.sortDir,
      shop_domain: getShopDomain(),
      platform: window.jdgmSettings?.platform || 'shopify'
    });

    if (reviewData.metafield_updated_at) params.set('ts', reviewData.metafield_updated_at);
    return getApiBase() + 'reviews/reviews_for_widget?' + params.toString();
  };

  const fetchReviewPage = async (page, sortKey = state.sort) => {
    const response = await fetch(buildReviewUrl(page, sortKey), {
      method: 'GET',
      headers: { Accept: 'application/json' }
    });

    if (!response.ok) throw new Error('Judge.me reviews request failed');
    return response.json();
  };

  state.reviews = dedupeReviews(state.reviews);

  const header = document.createElement('div');
  header.className = 'mma-judge__header';

  const intro = document.createElement('div');
  intro.className = 'mma-judge__intro';
  appendText(intro, 'span', 'mma-judge__eyebrow', 'Verified customer reviews');
  appendText(intro, 'h2', 'mma-judge__title', 'Real reviews from our customers');

  const score = document.createElement('div');
  score.className = 'mma-judge__score';
  appendText(score, 'span', 'mma-judge__score-value', Number(reviewData.average_rating || 0).toFixed(1));
  appendText(score, 'span', 'mma-judge__stars', stars(reviewData.average_rating));
  appendText(score, 'span', 'mma-judge__count', (reviewData.number_of_reviews || reviewData.reviews.length) + ' reviews');
  intro.appendChild(score);

  const histogram = document.createElement('div');
  histogram.className = 'mma-judge__histogram';
  (reviewData.histogram || []).forEach((item) => {
    const row = document.createElement('div');
    row.className = 'mma-judge__bar-row';
    appendText(row, 'span', 'mma-judge__bar-label', item.rating + '★');
    const track = document.createElement('span');
    track.className = 'mma-judge__bar-track';
    const fill = document.createElement('span');
    fill.className = 'mma-judge__bar-fill';
    fill.style.width = Math.max(0, Math.min(100, Number(item.percentage) || 0)) + '%';
    track.appendChild(fill);
    row.appendChild(track);
    appendText(row, 'span', 'mma-judge__bar-count', String(item.frequency || 0));
    histogram.appendChild(row);
  });

  const tools = document.createElement('div');
  tools.className = 'mma-judge__tools';
  tools.setAttribute('aria-label', 'Sort reviews');
  appendText(tools, 'span', 'mma-judge__sort-label', 'Sort reviews');
  const sortGroup = document.createElement('div');
  sortGroup.className = 'mma-judge__sort-group';
  sortGroup.setAttribute('role', 'group');
  sortGroup.setAttribute('aria-label', 'Review sort order');
  const sortButtons = {};
  Object.entries(sortOptions).forEach(([value, option]) => {
    const button = document.createElement('button');
    button.className = 'mma-judge__sort-button';
    button.type = 'button';
    button.dataset.mmaJudgeSort = value;
    button.textContent = option.label.replace(' reviews', '');
    button.setAttribute('aria-pressed', value === state.sort ? 'true' : 'false');
    sortButtons[value] = button;
    sortGroup.appendChild(button);
  });
  tools.appendChild(sortGroup);

  header.append(intro, histogram, tools);
  root.appendChild(header);

  const cards = document.createElement('div');
  cards.className = 'mma-judge__cards';
  cards.setAttribute('aria-live', 'polite');
  root.appendChild(cards);

  const buildCard = (review) => {
    const card = document.createElement('article');
    card.className = 'mma-review-card';

    const top = document.createElement('div');
    top.className = 'mma-review-card__top';
    appendText(top, 'div', 'mma-review-card__stars', stars(review.rating));
    appendText(top, 'time', 'mma-review-card__date', formatDate(review.created_at));
    card.appendChild(top);

    const person = document.createElement('div');
    person.className = 'mma-review-card__person';
    appendText(person, 'span', 'mma-review-card__avatar', review.reviewer_initial || (review.reviewer_name || 'C').charAt(0));
    const personCopy = document.createElement('div');
    appendText(personCopy, 'div', 'mma-review-card__name', review.reviewer_name || 'Customer');
    const meta = document.createElement('div');
    meta.className = 'mma-review-card__meta';
    if (review.verified_buyer) appendText(meta, 'span', 'mma-review-card__badge', 'Verified buyer');
    if (review.source === 'shop-app') appendText(meta, 'span', 'mma-review-card__badge', 'Shop App');
    if (review.location_country) appendText(meta, 'span', 'mma-review-card__badge', review.location_country);
    personCopy.appendChild(meta);
    person.appendChild(personCopy);
    card.appendChild(person);

    const title = stripHtml(review.title);
    const body = stripHtml(review.body_html || review.body);
    if (title) appendText(card, 'h3', 'mma-review-card__title', title);
    appendText(card, 'p', 'mma-review-card__body', body || (review.verified_buyer ? 'Rating-only review from a verified customer.' : 'Rating-only review from a customer.'));

    const media = document.createElement('div');
    media.className = 'mma-review-card__media';
    (review.pictures_urls || []).slice(0, 3).forEach((url) => {
      const imageUrl = typeof url === 'string' ? url : (url.compact || url.huge || url.original);
      if (!imageUrl || !/^https?:\/\//.test(imageUrl)) return;
      const image = document.createElement('img');
      image.src = imageUrl;
      image.alt = 'Customer review photo';
      image.loading = 'lazy';
      media.appendChild(image);
    });
    card.appendChild(media);

    const reply = stripHtml(review.reply_content);
    if (reply) {
      const replyBox = document.createElement('div');
      replyBox.className = 'mma-review-card__reply';
      appendText(replyBox, 'span', 'mma-review-card__reply-label', 'MMA Warehouse replied');
      appendText(replyBox, 'p', 'mma-review-card__reply-text', reply);
      card.appendChild(replyBox);
    }

    return card;
  };

  const actions = document.createElement('div');
  actions.className = 'mma-judge__actions';
  const status = appendText(actions, 'span', 'mma-judge__status', '');
  const loadButton = document.createElement('button');
  loadButton.className = 'mma-judge__load';
  loadButton.type = 'button';
  actions.appendChild(loadButton);
  root.appendChild(actions);

  const hasMoreReviews = () => {
    return state.visibleCount < state.reviews.length || state.page < state.totalPages;
  };

  const render = () => {
    cards.innerHTML = '';
    const visibleReviews = state.reviews.slice(0, state.visibleCount);

    visibleReviews.forEach((review) => {
      cards.appendChild(buildCard(review));
    });

    Object.entries(sortButtons).forEach(([sortKey, button]) => {
      const isActive = sortKey === state.sort;
      button.classList.toggle('is-active', isActive);
      button.setAttribute('aria-pressed', isActive ? 'true' : 'false');
      button.disabled = state.isLoading;
    });

    const total = Number(reviewData.number_of_reviews) || state.reviews.length;
    status.textContent = 'Showing ' + visibleReviews.length + ' of ' + total + ' reviews';
    loadButton.textContent = state.isLoading ? 'Loading reviews...' : 'Load 3 more reviews';
    loadButton.disabled = state.isLoading || !hasMoreReviews();
    loadButton.hidden = !hasMoreReviews() && visibleReviews.length > 0;
  };

  const loadUntilVisible = async (targetCount) => {
    while (state.reviews.length < targetCount && state.page < state.totalPages) {
      const nextPage = state.page + 1;
      const nextData = await fetchReviewPage(nextPage);
      state.page = Number(nextData.pagination?.current_page) || nextPage;
      state.totalPages = Number(nextData.pagination?.total_pages) || state.totalPages;
      state.reviews = dedupeReviews(state.reviews.concat(nextData.reviews || []));
    }
  };

  loadButton.addEventListener('click', async () => {
    if (state.isLoading) return;
    state.isLoading = true;
    render();

    try {
      const targetCount = state.visibleCount + 3;
      await loadUntilVisible(targetCount);
      state.visibleCount = Math.min(targetCount, state.reviews.length);
    } catch (error) {
      console.warn('MMA Judge.me reviews could not load more reviews', error);
      loadButton.textContent = 'Try loading again';
    } finally {
      state.isLoading = false;
      render();
    }
  });

  const setSort = async (nextSort) => {
    if (!sortOptions[nextSort]) return;
    if (state.isLoading || nextSort === state.sort) return;

    state.sort = nextSort;
    state.isLoading = true;
    state.visibleCount = 3;
    cards.classList.add('is-loading');
    render();

    try {
      const nextData = nextSort === 'latest' ? reviewData : await fetchReviewPage(1, nextSort);
      state.reviews = dedupeReviews(nextData.reviews || []);
      state.page = Number(nextData.pagination?.current_page) || 1;
      state.totalPages = Number(nextData.pagination?.total_pages) || 1;
      await loadUntilVisible(3);
    } catch (error) {
      console.warn('MMA Judge.me sort failed, using loaded review data', error);
      state.reviews = dedupeReviews(state.reviews).sort((a, b) => {
        const ratingDelta = Number(b.rating || 0) - Number(a.rating || 0);
        if (nextSort === 'highest' && ratingDelta) return ratingDelta;
        if (nextSort === 'lowest' && ratingDelta) return -ratingDelta;
        return new Date(b.created_at || 0) - new Date(a.created_at || 0);
      });
      state.page = 1;
      state.totalPages = 1;
    } finally {
      state.isLoading = false;
      cards.classList.remove('is-loading');
      render();
    }
  };

  sortGroup.addEventListener('click', (event) => {
    const button = event.target.closest('[data-mma-judge-sort]');
    if (!button) return;
    setSort(button.dataset.mmaJudgeSort);
  });

  render();

  widget.parentNode.insertBefore(root, widget);
}

function waitForMmaJudgeReviews(attempt = 0) {
  initMmaJudgeReviews();

  const enhanced = document.querySelector('.jdgm-review-widget[data-mma-enhanced="true"], #judgeme_product_reviews[data-mma-enhanced="true"]');
  if (!enhanced && attempt < 20) {
    window.setTimeout(() => waitForMmaJudgeReviews(attempt + 1), 250);
  }
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => waitForMmaJudgeReviews());
} else {
  waitForMmaJudgeReviews();
}

class UpsellCard extends HTMLElement {
  constructor() {
    super();
    this.select = this.querySelector('select');
    this.image = this.querySelector('.card-media img');

    if (this.select) {
      this.select.addEventListener('change', (event) => {
        const selectedOption = event.target.options[event.target.selectedIndex];
        const newImgSrc = selectedOption.getAttribute('data-variant-img-src');
        if (newImgSrc) { this.updateImage(newImgSrc) }
      });
    }
  }

  updateImage(newImgSrc) {
    if (this.image) {
      this.image.src = newImgSrc;
    }
  }
}

customElements.define('upsell-card', UpsellCard);
